/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import env from '../../../env';
import { useGet, SiteLink } from '../../../components';
import { Loading, ErrorPopup } from '../../../components';
import { useAuth } from '../../../auth';
import utils from '../../../utils';

const dashboardStyle = {
  dashboard: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '2rem 1rem',
  },
  header: {
    marginBottom: '2rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #dee2e6',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 600,
    marginBottom: '0.25rem',
  },
  section: {
    marginBottom: '2rem',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1rem',
  },
  orderCard: {
    marginBottom: '1.5rem',
    padding: '1.25rem',
    border: '1px solid #dee2e6',
    borderRadius: 6,
    backgroundColor: '#fff',
  },
  orderHeader: {
    marginBottom: '0.75rem',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '0.5rem 1rem',
  },
  orderNo: {
    fontWeight: 600,
  },
  orderMeta: {
    color: '#6c757d',
    fontSize: '0.95rem',
  },
  itemList: {
    listStyle: 'none',
    paddingLeft: 0,
    marginBottom: 0,
  },
  itemRow: {
    padding: '0.5rem 0',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
    borderBottom: '1px solid #f0f0f0',
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6c757d',
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
  },
};

/**
 * Member dashboard: purchase history and (optionally) account overview.
 * Shown at /members-area and /members-area/my-account.
 */
export function MemberDashboard() {
  const auth = useAuth();
  const get = useGet();

  React.useEffect(() => {
    get.send((env.apiBase || '').replace(/\/$/, '') + '/api/order/MyOrders');
  }, []);

  const displayName = auth.user?.displayName || auth.user?.email?.split('@')[0] || 'Member';

  return (
    <div css={dashboardStyle.dashboard}>
      <header css={dashboardStyle.header}>
        <h1 css={dashboardStyle.title}>My account</h1>
        <p className="text-muted" style={{ marginBottom: 0 }}>
          Welcome back, {displayName}. Here you can view your purchase history and access your streams.
        </p>
      </header>

      <section css={dashboardStyle.section} aria-labelledby="purchase-history-heading">
        <h2 id="purchase-history-heading" css={dashboardStyle.sectionTitle}>
          Purchase history
        </h2>

        {get.loading() && <Loading />}
        {get.hasErrors() && <ErrorPopup title="Error loading orders" errors={get.errors} />}

        {!get.loading() && !get.hasErrors() && (() => {
          const orders = Array.isArray(get.response) ? get.response : [];
          if (orders.length === 0) {
            return (
              <div css={dashboardStyle.emptyState}>
                <p style={{ marginBottom: '1rem' }}>You have no orders yet.</p>
                <SiteLink to="/live-streams" className="btn btn-outline-primary">
                  Browse streams
                </SiteLink>
              </div>
            );
          }

          return orders.map((order) => (
            <div key={order.orderId || order.OrderId} css={dashboardStyle.orderCard}>
              <div css={dashboardStyle.orderHeader}>
                <span css={dashboardStyle.orderNo}>Order {order.orderNo || order.OrderNo}</span>
                <span css={dashboardStyle.orderMeta}>
                  {utils.date.formatYYYYMMDD(order.orderDate || order.OrderDate)}
                </span>
                <span css={dashboardStyle.orderMeta}>
                  AU {utils.money.formatMoney(order.totalAmount || order.TotalAmount)}
                </span>
              </div>
              <ul css={dashboardStyle.itemList}>
                {(order.orderItems || order.OrderItems || []).map((item) => {
                  const watchToken = item.watchToken || item.WatchToken;
                  const name = item.itemName || item.ItemName || 'Item';
                  return (
                    <li key={item.orderItemId || item.OrderItemId} css={dashboardStyle.itemRow}>
                      <span>{name}</span>
                      {watchToken && (
                        <SiteLink
                          to={`/watch?t=${encodeURIComponent(watchToken)}`}
                          className="btn btn-primary btn-sm"
                        >
                          Watch
                        </SiteLink>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ));
        })()}
      </section>
    </div>
  );
}
